import { memo, useCallback, useLayoutEffect, useState } from 'react'

import { PersonAddAlt1, Refresh } from '@mui/icons-material'
import { Button, Tooltip } from '@mui/material'
import { OrgEnvironmentRoleRamMemberRolesEnum } from '@nativewaves/nw-cloud-sdk/org'
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { AnimatedCounterButton } from '@shared/components/MaterialUIEnhancements/IconButton'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, ListOperationBox } from '@shared/layouts'
import { RowActions, useColumns } from 'domains/Environment'
import { InvitationActionButton } from 'domains/Environment/Invitations'
import { UserDialogContent } from 'domains/Environment/Users'
import { useEnvironmentMemberUpdateMutation } from 'hooks/mutations/org'
import { usersOverviewRoute } from 'pages/NWPlatform/Environment'
import { environmentQueryKeys } from 'services/queryKeys'
import { resolveDisplayName } from 'utils/userInfo'

const OverviewRaw = () => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const { environmentId } = usersOverviewRoute.useParams()
  const navigate = useNavigate({ from: usersOverviewRoute.fullPath })
  const { query } = usersOverviewRoute.useSearch()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])

  const environmentMemberUsersQuery = useInfiniteQuery({
    ...environmentQueryKeys.memberList({ environmentId, memberType: 'User', query }),
    refetchOnMount: 'always',
  })
  const userMembers = environmentMemberUsersQuery.data?.pages.flatMap((page) => page.items)

  const { mutate: updateUser, ...environmentMemberUpdateMutation } = useEnvironmentMemberUpdateMutation()

  const handleUpdate = useCallback(
    ({ memberId, roles }: { memberId: string; roles: OrgEnvironmentRoleRamMemberRolesEnum[] }) => {
      updateUser({
        environmentId,
        memberId,
        orgEnvironmentRoleRamMemberEdit: {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          roles,
        },
      })
    },
    [environmentId, updateUser],
  )

  const columns = useColumns({
    onUpdate: handleUpdate,
  })

  useLayoutEffect(() => {
    if (environmentMemberUpdateMutation.isPending) {
      setLoadingRows([environmentMemberUpdateMutation.variables.memberId])

      return () => setLoadingRows([])
    }
  }, [environmentMemberUpdateMutation.isPending, environmentMemberUpdateMutation.variables?.memberId])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:EnvironmentSetting.searchUsers')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <ListOperationBox>
          <Tooltip title={t('refresh')} placement="left">
            <AnimatedCounterButton
              action={useCallback(
                () =>
                  queryClient.invalidateQueries({
                    queryKey: environmentQueryKeys.memberList({ environmentId, memberType: 'Team' }).queryKey,
                  }),
                [environmentId, queryClient],
              )}
            >
              {(props) => <Refresh {...props} />}
            </AnimatedCounterButton>
          </Tooltip>

          <InvitationActionButton size="large" />

          <Button variant="contained" endIcon={<PersonAddAlt1 />} onClick={() => setDialogOpen(true)}>
            {t('domain:EnvironmentSetting.addUser')}
          </Button>
        </ListOperationBox>
      </ActionStrip>

      <Table
        data={userMembers ?? []}
        columns={columns}
        isInitialLoadingGlobal={environmentMemberUsersQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: ['roles'] }), {})}
        ActionMenuProps={(row) => ({
          title: resolveDisplayName(row.original.userInfo) || '',
          entity: t('entity:org.member', 'Member', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
        tableOptions={{
          getRowId: (member) => member.id,
        }}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <UserDialogContent />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
